// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

const rootElement: HTMLElement = (() => {
  const candidate = document.querySelector<HTMLElement>("#app");
  if (!candidate) throw new Error("Root element not found");
  return candidate;
})();

function renderFatalBootstrapError(error: unknown): void {
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  document.title = "Reginub Admin configuration error";
  rootElement.replaceChildren();
  const section = document.createElement("section");
  section.setAttribute("role", "alert");
  section.style.cssText =
    "max-width:48rem;margin:4rem auto;padding:2rem;font-family:system-ui,sans-serif;color:#8b0000;background:#fff0f0;border:2px solid #8b0000;border-radius:0.75rem";
  const heading = document.createElement("h1");
  heading.textContent = "Reginub Admin could not start safely";
  const explanation = document.createElement("p");
  explanation.textContent =
    "The Admin configuration is missing, malformed, or belongs to another environment. No server request was made.";
  const details = document.createElement("pre");
  details.textContent = message;
  details.style.whiteSpace = "pre-wrap";
  section.append(heading, explanation, details);
  rootElement.append(section);
}

async function start(): Promise<void> {
  // Configuration is deliberately loaded before the application graph. If it
  // fails, the fatal screen below is the only UI that is initialized.
  await import("@/config");
  const [{ createRoot, hydrateRoot }, { App }, { preloadLocale }, { router }] =
    await Promise.all([
      import("react-dom/client"),
      import("@/app"),
      import("@/intl"),
      import("@/router"),
    ]);

  // If there is something in the root element, hydrate, else fall back to
  // creating a new root.
  if (rootElement.innerHTML) {
    // This will trigger the loading everything in the router.
    // We do this before trying to render/hydrate, to avoid flickering to a blank
    // screen (which TanStack Router will do if it is loading the matches)
    await Promise.all([
      // Preload the locale data
      preloadLocale(),
      // Preload anything from the router
      router.load(),
    ]);

    hydrateRoot(rootElement, <App />, {
      onRecoverableError: (error) => {
        // React doesn't like that we use `renderToString` to render a
        // suspense boundary fallback. This is very much intentional, so we
        // can safely ignore this
        if (error instanceof Error && error.message.includes("#419")) {
          return;
        }

        console.error(error);
      },
    });
  } else {
    // This is the fallback during development, when we don't have a pre-rendered
    // spinner. We want to start rendering as soon as possible so that devtools
    // get rendered; we don't care about pre-loading the router.
    const root = createRoot(rootElement);

    root.render(<App />);
  }
}

try {
  await start();
} catch (error) {
  console.error(error);
  renderFatalBootstrapError(error);
}
